import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';
import Drawer from '@material-ui/core/Drawer';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Badge from '@material-ui/core/Badge';
import { withStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';

import { withConfig } from 'Context/ConfigContext';
import { withOffset } from 'Context/LayoutContext';

import { useNotificationTracking } from '../../../Util/Analytics';
import Colors from '../../Theme/colors';
import { Box, Flex, SvgIcon, Text } from '../../Base';
import TabContent from '../TabContent';

const Container = styled(Flex)`
  ${({ isRedirecting }) =>
    css`
      position: relative;
      flex-direction: column;
      justify-content: flex-start;
      flex-grow: 1;
      width: ${isRedirecting ? '100vw' : 'calc(6 / 7 * 100vw)'};
      max-width: ${isRedirecting ? '100%' : '375px'};
      height: 100%;
    `}
`;

const StyledTab = styled(Tab)`
  flex-grow: 1;
`;

const StyledBadge = withStyles(() => ({
  badge: {
    right: -10,
    backgroundColor: Colors.badge,
    color: Colors.white,
  },
}))(Badge);

const StyledBox = styled(Box)`
  box-shadow: 0px 1px ${({ theme }) => theme.colors.notificationDivider};
  margin-bottom: 1px;
  flex-shrink: 0;
`;

const ClearLoading = () => {
  return (
    <Flex flexDirection={'column'} justifyContent={'center'} alignItems={'center'} height={'100vh'}>
      <Box mb={6}>
        <SvgIcon
          color={'dangerLight'}
          icon={SvgIcon.ICON_LIST.LOADING}
          width={'60px'}
          height={'60px'}
        />
      </Box>
      <Text textStyle={'heading4'} color={'dangerLight'} textAlign={'center'} fontWeight={'normal'}>
        เรากำลังพาคุณไประบบ CLEAR...
      </Text>
    </Flex>
  );
};

const Inbox = ({
  appConfig,
  open,
  onClose,
  onReadNotification,
  news,
  reminders,
  courses,
  t,
  onRedirectUrl,
  isRedirecting,
  loading,
  hasMoreNews,
  onLoadMoreNews,
}) => {
  const { uiVariant } = appConfig;
  const [value, setValue] = useState(0);
  const [notiNewsCount, setNotiNewsCount] = useState(0);
  const [notiReminderCount, setNotiReminderCount] = useState(0);
  const { logNotificationTabClickEvent } = useNotificationTracking();

  useEffect(() => {
    setNotiNewsCount(news.filter((content) => content.isNew).length);
  }, [news]);

  useEffect(() => {
    setNotiReminderCount(reminders.filter((content) => content.isNew).length);
  }, [reminders]);

  const handleChange = (event, newValue) => {
    const tabLabel = newValue === 0 ? t('TAB1') : t('TAB2');
    logNotificationTabClickEvent(tabLabel);
    setValue(newValue);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Container isRedirecting={isRedirecting}>
        {uiVariant === 'PAAS' ? (
          <Fragment>
            <StyledBox pt={8} pb={6} px={4} bg={'white'}>
              <Text textStyle={'heading4'}>{t('HEADER')}</Text>
            </StyledBox>
            <TabContent hidden={false} contents={[...courses, ...news, ...reminders]} />
          </Fragment>
        ) : isRedirecting ? (
          <ClearLoading />
        ) : (
          <Fragment>
            <Box
              pt={'APP_TOP_OFFSET'}
              pb={5}
              px={4}
              mt={8}
              mb={3}
              bg={'white'}
              style={{ flexShrink: 0 }}
            >
              <Text textStyle={'heading2'}>{t('HEADER')}</Text>
            </Box>
            <Tabs
              TabIndicatorProps={{
                style: { backgroundColor: Colors.black },
              }}
              value={value}
              onChange={handleChange}
              variant="fullWidth"
            >
              <StyledTab
                label={
                  notiNewsCount > 0 ? (
                    <StyledBadge badgeContent={notiNewsCount}>
                      <Text textStyle={'heading5'}>{t('TAB1')}</Text>
                    </StyledBadge>
                  ) : (
                    <Text textStyle={'heading5'}>{t('TAB1')}</Text>
                  )
                }
              />
              <StyledTab
                label={
                  notiReminderCount > 0 ? (
                    <StyledBadge badgeContent={notiReminderCount}>
                      <Text textStyle={'heading5'}>{t('TAB2')}</Text>
                    </StyledBadge>
                  ) : (
                    <Text textStyle={'heading5'}>{t('TAB2')}</Text>
                  )
                }
              />
            </Tabs>
            <TabContent
              hidden={value !== 0}
              contents={news}
              onContentClick={() => {
                onReadNotification();
                setNotiNewsCount(notiNewsCount - 1);
              }}
              onRedirectUrl={onRedirectUrl}
              loading={loading}
              hasMore={hasMoreNews}
              onLoadMore={onLoadMoreNews}
            />
            <TabContent
              hidden={value !== 1}
              contents={reminders}
              onContentClick={() => {
                onReadNotification();
                setNotiReminderCount(notiReminderCount - 1);
              }}
            />
          </Fragment>
        )}
      </Container>
    </Drawer>
  );
};

Inbox.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onReadNotification: PropTypes.func,
  news: PropTypes.array,
  reminders: PropTypes.array,
  courses: PropTypes.array,
  onRedirectUrl: PropTypes.func,
  isRedirecting: PropTypes.bool,
  loading: PropTypes.bool,
  hasMoreNews: PropTypes.bool,
  onLoadMoreNews: PropTypes.func,
};

Inbox.defaultProps = {
  open: false,
  onClose: null,
  onReadNotification: null,
  news: [],
  reminders: [],
  courses: [],
  onRedirectUrl: null,
  isRedirecting: false,
  loading: false,
  hasMoreNews: false,
  onLoadMoreNews: null,
};

export default compose(withConfig, withTranslation('inbox'), withOffset)(Inbox);
